import React, { useState } from "react";
import { formatMoney } from "../../../../utils/fashion/money";
import { LINE_DISCOUNT_REASONS, approvalLimitNote, lineDiscountRules, needsApproval, presetValue, previewLineDiscount } from "../../../../utils/fashion/lineDiscount";

// Discount on the selected bag line, set in place: % or ₹, a reason and the
// price it leaves. Applying goes through Core, which asks for a manager when
// the rule's approval band says so. Reopened on a discounted line, it starts
// from what was given. Escape closes it.
export default function LineDiscountEditor({ line, rules, tillData, current, onApply, onClose }) {
  const { pct, flat } = lineDiscountRules(rules);
  const [mode, setMode] = useState(current?.mode === "flat" && flat ? "flat" : pct ? "pct" : "flat");
  const [value, setValue] = useState(current?.value ? String(current.value) : "");
  const [reason, setReason] = useState(current?.reason || "");
  const [tried, setTried] = useState(false);

  if (!pct && !flat) {
    return (
      <div className="fp-ld" role="group" aria-label="Line discount" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <p className="fp-ld-none fp-muted">No line discounts are set up for this store.</p>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  const rule = mode === "pct" ? pct : flat;
  const preset = presetValue(rule);
  const amount = preset !== null ? String(preset) : value;
  const preview = previewLineDiscount(line, mode, amount);
  const currSymbol = tillData?.tillAccess?.csBunit?.currencies?.[0]?.currSymbol || "";
  const note = needsApproval(rule, amount) ? "A manager approves this one." : approvalLimitNote(rule, currSymbol);
  const showError = tried && preview.error;

  const submit = (e) => {
    e.preventDefault();
    setTried(true);
    if (preview.error || !reason) return;
    onApply({ rule, mode, value: String(Number(amount)), reason });
  };
  const onKeyDown = (e) => {
    if (e.key !== "Escape") return;
    e.stopPropagation();
    onClose();
  };

  return (
    <form className="fp-ld" aria-label={`Discount ${line.name}`} onSubmit={submit} onKeyDown={onKeyDown}>
      <div className="fp-ld-row">
        {pct && flat && (
          <span className="fp-ld-mode" role="group" aria-label="Discount type">
            <button type="button" className={`fp-chip${mode === "pct" ? " is-on" : ""}`} aria-pressed={mode === "pct"} onClick={() => setMode("pct")}>
              %
            </button>
            <button type="button" className={`fp-chip${mode === "flat" ? " is-on" : ""}`} aria-pressed={mode === "flat"} onClick={() => setMode("flat")}>
              {currSymbol || "Amount"}
            </button>
          </span>
        )}
        <label className="fp-lbl fp-muted" htmlFor="fp-ld-value">
          {mode === "pct" ? "Percent off" : "Amount off"}
        </label>
        <input
          id="fp-ld-value"
          className="fp-qty-input fp-num"
          inputMode="decimal"
          value={amount}
          readOnly={preset !== null}
          onChange={(e) => setValue(e.target.value)}
          aria-invalid={showError ? "true" : "false"}
          aria-describedby="fp-ld-msg"
        />
      </div>
      <div className="fp-ld-reasons" role="group" aria-label="Reason">
        {LINE_DISCOUNT_REASONS.map((r) => (
          <button key={r} type="button" className={`fp-chip${reason === r ? " is-on" : ""}`} aria-pressed={reason === r} onClick={() => setReason(r)}>
            {r}
          </button>
        ))}
      </div>
      <p id="fp-ld-msg" className={`fp-ld-msg fp-num${showError || (tried && !reason) ? " is-bad" : ""}`} aria-live="polite">
        {showError
          ? preview.error
          : tried && !reason
            ? "Pick a reason."
            : preview.error
              ? note || " "
              : `${formatMoney(preview.gross, tillData)} → ${formatMoney(preview.after, tillData)} · saves ${formatMoney(preview.off, tillData)}${note ? ` · ${note}` : ""}`}
      </p>
      <div className="fp-ld-row">
        <button type="submit" className="fp-btn-primary fp-btn-sm">
          Apply discount
        </button>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
